import { forwardRef, useEffect, useMemo, useState } from 'react';

import pluralize from 'pluralize';

import {
  Button,
  Column,
  DataTable,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerOverlayProvider,
  DrawerProps,
  Icons,
  Radio,
  TableHeader,
  TableRecordsCount,
  useDrawer,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';

import { CloseDrawerGuard } from 'components/drawer';
import { Link } from 'components/router';

import { getPreselectedPolicyId } from './utils';
import { PolicyAssignmentConfirmationOverlay } from '../../../../_components/overlay';
import { WarningTooltip } from '../../../../_components/WarningTooltip';
import { ScalingPolicyWithWorkloads } from '../../../../_types';
import { makeEditScalingPolicySettingsLink } from '../../../../navigation';
import { FormDrawerFooter } from '../../../_components/FormDrawerFooter';
import { useScalingPoliciesWithWorkloads } from '../../../_hooks/useScalingPoliciesWithWorkloads';

type BaseProps = { workloadIds: string[] };
type Props = DrawerProps & BaseProps;

const AssignPolicyDrawer = forwardRef<HTMLDivElement, Props>(
  ({ workloadIds, ...props }: Props, ref) => {
    const { policies, isLoading } = useScalingPoliciesWithWorkloads();

    const [selectedPolicyId, setSelectedPolicyId] = useState<
      string | undefined
    >();

    const initialPolicyId = useMemo(() => {
      return policies
        ? getPreselectedPolicyId(workloadIds, policies)
        : undefined;
    }, [workloadIds, policies]);

    // Preselect policy if all workloads are assigned to the same policy
    useEffect(() => {
      if (!selectedPolicyId && initialPolicyId) {
        setSelectedPolicyId(initialPolicyId);
      }
    }, [initialPolicyId, selectedPolicyId]);

    const selectedPolicy = selectedPolicyId
      ? policies?.find(({ id }) => id === selectedPolicyId)
      : undefined;

    const wasPolicyChanged =
      !!selectedPolicy && selectedPolicyId !== initialPolicyId;

    return (
      <Drawer ref={ref} size="small" testId="assign-scaling-policy" {...props}>
        <DrawerOverlayProvider>
          {({ showOverlay, destroyOverlay }) => (
            <>
              <DrawerHeader />
              <Stack flexGrow={1} overflow="hidden">
                <DrawerContent>
                  <Typography
                    variant="L12R"
                    color={tokens.theme.fg.structural.secondary}
                    mb={4}
                  >
                    Assign scaling policy:
                  </Typography>
                  <Typography variant="h5" mb={24}>
                    {workloadIds.length}{' '}
                    {pluralize('workload', workloadIds.length)} selected
                  </Typography>
                  <TableHeader
                    recordsCount={
                      <TableRecordsCount
                        title="policy"
                        outOf={policies?.length}
                      />
                    }
                  />
                </DrawerContent>
                <Box flexGrow={1} minHeight={0}>
                  <DataTable<ScalingPolicyWithWorkloads>
                    data={policies}
                    isLoading={isLoading}
                    fillParentHeight
                    sizing="s"
                    rowHeight={32}
                    onRowClick={(event, { row }) => {
                      if (
                        !row.isCastware &&
                        // Can't set 'preventDefault' on expander, because already opened subdrawer is not closed
                        !(event as any).isIconClick
                      ) {
                        setSelectedPolicyId(row.id);
                      }
                    }}
                    footer={
                      wasPolicyChanged ? (
                        <FormDrawerFooter
                          pt={34}
                          saveButtonProps={{
                            onClick: () =>
                              showOverlay({
                                content: (
                                  <PolicyAssignmentConfirmationOverlay
                                    policy={selectedPolicy}
                                    workloadIds={workloadIds}
                                    title="Are you sure you want to assign the workloads to the scaling policy?"
                                    onCancel={destroyOverlay}
                                  />
                                ),
                              }),
                          }}
                        />
                      ) : undefined
                    }
                    getRowClass={({ isCastware }) =>
                      isCastware ? 'castware' : undefined
                    }
                    sx={{
                      '& .castware .DS-Table-Row': { cursor: 'initial' },
                    }}
                    testId="scaling-policies-batch-assignment-table"
                  >
                    <Column<ScalingPolicyWithWorkloads>
                      id="name"
                      header="Policy name"
                      width={355}
                      renderCell={({ name, id, isCastware }) => (
                        <Stack direction="row" alignItems="center" gap={16}>
                          <WarningTooltip
                            title={`${name} cannot be assigned as it is a castware scaling policy.`}
                            show={isCastware}
                          >
                            <span>
                              <Radio
                                size="small"
                                checked={id === selectedPolicyId}
                                testId="policy-radio"
                                disabled={isCastware}
                              />
                            </span>
                          </WarningTooltip>

                          <Button
                            testId="scaling-policy-name"
                            variant="text"
                            endIcon={<Icons.ArrowSquareOut size={14} />}
                            component={Link}
                            to={makeEditScalingPolicySettingsLink({
                              policyId: id,
                            })}
                            rel="noopener noreferrer"
                            target="_blank"
                            onClick={(event: any) => {
                              event.stopPropagation();
                            }}
                          >
                            {name}
                          </Button>
                        </Stack>
                      )}
                      cellWrapperSx={{ pl: 32 }}
                      sortable
                    />
                    <Column<ScalingPolicyWithWorkloads>
                      id="workloadsCount"
                      header="Workloads"
                      width={121}
                      renderCell={({ workloads }) => workloads.length}
                      sortable
                      accessor="workloads.length"
                    />
                  </DataTable>
                </Box>
              </Stack>
              <CloseDrawerGuard formGuard={false} isDirty={wasPolicyChanged} />
            </>
          )}
        </DrawerOverlayProvider>
      </Drawer>
    );
  }
);

AssignPolicyDrawer.displayName = 'AssignPolicyDrawer';

export const useAssignPolicyDrawer = () => {
  const { open } = useDrawer();
  return {
    openAssignPolicyDrawer: (props: BaseProps) => {
      open({ component: AssignPolicyDrawer, props });
    },
  };
};
